import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Search } from "lucide-react";
import checkchallanImage from "../../assets/challan-online.png";
import indIcon from "../../assets/Ind.png";
import styles from "./searchDetails.module.css";
import TopServices from "./TopServices";
import LoanCard from "../../components/LoanCard";
import vehicleIcon from "../../assets/vehicleLoan.png";
import instantIcon from "../../assets/instantLoan.png";
import personalIcon from "../../assets/personalLoan.png";
import refinanceIcon from "../../assets/refinanceLoan.png";
import RCDetails from "./RCDetails";
import FAQSection from "../../components/FAQSection";
import { isAuthenticated } from "../../components/AuthUtils";

function SearchDetails() {
  const navigate = useNavigate();
  const location = useLocation();
  const [vehicleNumber, setVehicleNumber] = useState(location.state?.vehicleNumber || "");
  const [error, setError] = useState("");
  const [shouldFetchRC, setShouldFetchRC] = useState(false);
  const rcDetailsRef = useRef(null);

  const userLoggedIn = isAuthenticated();

  const validateVehicleNumber = (number) => {
    const cleanedNumber = number.replace(/\s/g, "").toUpperCase();
    const regex = /^[A-Z]{2}[0-9]{1,2}[A-Z]{0,3}[0-9]{1,4}$/;
    const validStateCodes = [
      "AN", "AP", "AR", "AS", "BR", "CH", "CG", "DD", "DL", "GA", "GJ", "HR", "HP", "JH", "JK", "KA", "KL",
      "LD", "MP", "MH", "MN", "ML", "MZ", "NL", "OD", "PY", "PB", "RJ", "SK", "TN", "TS", "TR", "UP", "UK", "WB"
    ];

    if (!regex.test(cleanedNumber)) {
      return "Invalid vaahan number format. Please enter a valid Indian vaahan number (e.g., DL3SDW2595, UP57BX1666).";
    }

    const stateCode = cleanedNumber.substring(0, 2);
    if (!validStateCodes.includes(stateCode)) {
      return "Invalid state code. Please use a valid Indian state code (e.g., DL, MH, UP).";
    }

    return "";
  };

  // Auto-search effect when navigating from Home
  useEffect(() => {
    if (location.state?.vehicleNumber) {
      const vehicleNum = location.state.vehicleNumber;
      setVehicleNumber(vehicleNum);
      // Small delay to ensure state is set before triggering search
      setTimeout(() => {
        handleSearch(vehicleNum);
      }, 100);
    }
  }, [location.state]);

  const handleSearch = async (manualNumber) => {
    const inputNumber = manualNumber || vehicleNumber;
    const cleanedNumber = inputNumber.replace(/\s+/g, '').toUpperCase();

    if (!cleanedNumber) {
      setError('Please enter a vehicle number');
      return;
    }

    const validationError = validateVehicleNumber(cleanedNumber);
    if (validationError) {
      setError(validationError);
      return;
    }

    setError('');
    setShouldFetchRC(true);

    // Scroll to details view
    setTimeout(() => {
      if (rcDetailsRef.current) {
        rcDetailsRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 100);
  };

  // Loans data to be shown only if user is logged in
  const loans = [
    { id: 1, title: "Vaahan Loan", image: vehicleIcon, path: "/loan" },
    { id: 2, title: "Instant Loan", image: instantIcon, path: "/loan" },
    { id: 3, title: "Personal Loan", image: personalIcon, path: "/loan" },
    { id: 4, title: "Refinance Loan", image: refinanceIcon, path: "/loan" },
  ];

  // On loan card click, check auth and redirect to login if needed
  const onLoanCardClick = (path) => {
    if (!userLoggedIn) {
      navigate('/phone');
      return;
    }
    navigate(path);
  };

  return (
    <div className={styles.mainContainer}>
      <div className={styles.container}>
        <div className={styles.leftContent}>
          <div className={styles.headerGroup}>
            <h1 className={styles.title}>
              Check Your <span className={styles.highlight}>RC Details</span> Online
            </h1>
            <p className={styles.subtitle}>
              Check your vaahan RC Details online in one click to stay updated
            </p>
          </div>

          <div className={styles.searchBox}>
            <div className={styles.searchContainer}>
              <div className={styles.indSection}>
                <div className={styles.prefix}>
                  <img src={indIcon} alt="IND" />
                </div>
              </div>
              <input
                type="text"
                placeholder="e.g., MH15CV2211, DL12AA1234"
                className={styles.searchInput}
                value={vehicleNumber}
                onChange={(e) => {
                  setVehicleNumber(e.target.value.toUpperCase());
                  setError("");
                  setShouldFetchRC(false);
                }}
                onKeyPress={(e) => {
                  if (e.key === "Enter") {
                    handleSearch();
                  }
                }}
              />
              <button className={styles.searchButton} onClick={handleSearch}>
                <Search size={20} />
              </button>
            </div>
            {error && <p className={styles.errorMessage} style={{ color: "red", marginTop: "10px" }}>{error}</p>}
          </div>
        </div>

        <div className={styles.rightContent}>
          <div className={styles.imageWrapper}>
            <div className={styles.blueCircle}></div>
            <img
              src={checkchallanImage}
              alt="Traffic officer checking vehicle with driver using smartphone app"
              className={styles.image}
            />
          </div>
        </div>
      </div>

      {/* Show TopServices and Loans ONLY if the user is authenticated */}
      {userLoggedIn && <TopServices />}

      <div ref={rcDetailsRef}>
        <RCDetails
          vehicleNumber={vehicleNumber.replace(/\s+/g, '').toUpperCase()}
          shouldFetch={shouldFetchRC}
          isAuthenticated={userLoggedIn} // PASS AUTH STATUS TO RCDetails
        />
      </div>

      {/* Loans section is visible only if logged in */}
      {userLoggedIn && (
        <div className="loan-section">
          <h2 className="loan-header">Loans</h2>
          <div className="loan-scroll">
            {loans.map((loan) => (
              <LoanCard
                key={loan.id}
                title={loan.title}
                image={loan.image}
                onClick={() => onLoanCardClick(loan.path)}
              />
            ))}
          </div>
        </div>
      )}

      {/* FAQ Section is also auth protected */}
      {userLoggedIn && <FAQSection />}
    </div>
  );
}

export default SearchDetails;
